import React, { useCallback, useEffect } from "react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { skinVars,IconLockClosedLight } from "@telefonica/mistica";
import { strings } from "../../../localization";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { Store } from "./store";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { TabSelectedEnum } from "../../../stores";
import { useNavigateCustom } from "../../../hooks";
import { ValidationOTPToken } from "../../../components/templates";
import { ValidationTypeOTP } from "../../../enum";

const { title, description } = strings.pages.signContract.OPTConfirmation;
const { confirmSign } = strings.components.buttons;

const SignContract: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { authenticationStore, anticipationStore, paymentInfo, validationOTPStore, fintechRegisterStore } = useGlobalStore();
	const { addAnalyticEvent, updatePageView, addAnalyticEventPurchase } = useAnalytics();
	const isPartial = anticipationStore.getTabSelected() === TabSelectedEnum.PARTIAL;

	const defaultLabels = strings.organisms.pinInput;

	const handleFinishSignContract = useCallback((): void => {
		// Substitui a rota atual pela raiz
		navigate("/", { replace: true });

		// Em seguida, navega para a rota desejada
		navigate("/anticipation-finished", { replace: true });

		const valueDisbursed = isPartial
			? anticipationStore.partialAnticipationShelf.partialSimulationData.value?.disbursedIssueAmount
			: anticipationStore.totalAnticipationShelf.disbursedIssueAmount.value;

		addAnalyticEventPurchase({
			event: "purchase",
			item: {
				item_name: `antecipacao-${isPartial ? "parcial" : "total"}-fgts`,
				price: valueDisbursed,
			},
		});
	},[]);

	const store = useLocalObservable(
		() => new Store(authenticationStore, anticipationStore, paymentInfo, validationOTPStore, fintechRegisterStore, handleFinishSignContract),
	);

	React.useEffect(() => {
		updatePageView({
			name: "Vivo Money FGTS Confirmar Contratação Senha",
			pagePath: "/fgts/confirmar-contratacao/senha",
		});
	}, []);

	React.useEffect(() => {
		navigator.permissions.query({ name: "geolocation" }).then((result) => {
			if (result.state === "granted") {
				navigator.geolocation.getCurrentPosition((pos) =>
					store.location.set({ lat: pos.coords.latitude, long: pos.coords.longitude }),
				);
			} else if (result.state === "prompt") {
				navigator.geolocation.getCurrentPosition((pos) =>
					store.location.set({ lat: pos.coords.latitude, long: pos.coords.longitude }),
				);
			}
		});
	}, []);

	React.useEffect(() => {
		if (store.attempts.value === 0) {
			navigate("/access-blocked", { replace: true });
		}
	}, [store.attempts.value]);

	const onHandleSignContract = async (): Promise<void> => {
		await store.postOtpValidate.fetch();
	};

	const onHandleGoBack = (): void => {
		navigate(-1);
	};

	useEffect(() => {
		if (validationOTPStore.remainingAttempts < 3) {
			addAnalyticEvent({
				event: "non_interaction",
				action: "view-error",
				step: "fgts:confirmar-contracao:senha",
				description: defaultLabels.errorField(validationOTPStore.remainingAttempts),
			}, { applySanitize: true });
		}

	}, [validationOTPStore.remainingAttempts]);

	const handleSendValidationOTP = useCallback(() => {
		store.codeOtp.set("");
		validationOTPStore.resetRemainingAttempts();
		validationOTPStore.sendValidationOTP(ValidationTypeOTP.PHONE);
	}, [validationOTPStore.resetRemainingAttempts, validationOTPStore.sendValidationOTP]);

	return (
		<ValidationOTPToken
			title={title}
			description={description(fintechRegisterStore.getPhoneNumber)}
			buttonPrimaryLabel={confirmSign}
			isDisabled={store.isLoading || store.attempts.value <= 0}
			isDisabledInput={store.attempts.value <= 0}
			codeOTP={store.codeOtp.value}
			icon={<IconLockClosedLight size={64} color={skinVars.colors.brand} />}
			setCodeOtp={(code: string) => {
				store.codeOtp.set(code);
			}}
			isLoading={store.isLoading}
			onBack={onHandleGoBack}
			onResend={handleSendValidationOTP}
			onContinue={onHandleSignContract}
			attempts={store.attempts.value}
		/>
	);
});

export default SignContract;
